import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantAuthStore } from '../state/tenantAuth';
import { tenantApi } from '../services/tenantApi';
import { toast } from 'sonner';
import type { Employee, Tenant } from '../state/tenantAuth';
import type { PermissionName } from '../types/permissions';
import { usePreferencesStore } from '@/state/preferences';
import type { PreferencesTree } from '@/types/preferences';
import { purgePendingBoatChanges } from '@/modules/inventory/utils/pending-boat-changes';

interface LoginCredentials {
  email: string;
  password: string;
  remember?: boolean;
}

/**
 * Discriminated union returned by the /auth/login endpoint.
 *
 * When the employee has 2FA enabled, the backend returns only {requires_2fa: true}
 * and does NOT establish a session. The frontend must prompt for a TOTP code
 * and POST it to /auth/2fa/challenge to complete the login.
 */
export type LoginResponse =
  | { requires_2fa: true }
  | { requires_2fa?: false; employee: Employee; tenant: Tenant };

export function isTwoFactorChallenge(
  response: LoginResponse,
): response is { requires_2fa: true } {
  return (response as { requires_2fa?: boolean }).requires_2fa === true;
}

interface TwoFactorChallengePayload {
  code?: string;
  recovery_code?: string;
}

interface AuthenticatedLoginResponse {
  employee: Employee;
  tenant: Tenant;
}

/**
 * Get current tenant auth state
 */
export const useTenantAuth = () => tenantAuthStore();

/**
 * Get current employee
 */
export const useEmployee = () => tenantAuthStore((state) => state.employee);

/**
 * Get current tenant
 */
export const useTenant = () => tenantAuthStore((state) => state.tenant);

/**
 * D9-08 (#408) — check whether the current employee holds one or all of the
 * given permission name(s), derived from their roles via `/auth/me`.
 *
 * IMPORTANT: this is a UX convenience only, not an enforcement point. It
 * decides whether to render, disable, or hide an affordance — the server's
 * own policies and FormRequests remain the sole authority, and a caller who
 * bypasses this check still gets a 403 from the API. Never treat a `true`
 * result here as proof an action is authorized.
 *
 * Semantics: an array requires ALL listed permissions (all-of); a single
 * string checks membership; an absent permission list (no employee, or an
 * older cached auth payload without the field) always returns `false`.
 *
 * D-26 (#1441): the argument is typed with the generated `PermissionName`,
 * so a misspelt or retired literal fails `npm run type-check`. The held list
 * stays `string[]`, and any string not held — including a name this bundle
 * does not know — is treated as not held (ADR 0017 statement 8).
 */
export function useCan(permission: PermissionName | readonly PermissionName[]): boolean {
  const employee = useEmployee();
  const held = employee?.permissions;

  if (!held) {
    return false;
  }

  const heldSet = new Set<string>(held);

  // `typeof` rather than `Array.isArray`: the latter cannot narrow a
  // `readonly PermissionName[]` union member away in its false branch.
  if (typeof permission === 'string') {
    return heldSet.has(permission);
  }

  return permission.every((p) => heldSet.has(p));
}

/**
 * Tenant login hook with Laravel Sanctum SPA authentication.
 *
 * Two-phase flow when 2FA is enabled:
 *   Phase 1: POST /auth/login → {requires_2fa: true}. Zustand is NOT updated.
 *            The caller inspects `data.requires_2fa` and shows a TOTP prompt.
 *   Phase 2: POST /auth/2fa/challenge via {@link useChallenge2FA} → {employee, tenant}.
 *            That hook completes the Zustand login.
 *
 * When 2FA is NOT enabled, phase 1 returns the full payload and Zustand is
 * updated immediately, matching the pre-2FA behavior.
 */
export function useTenantLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      return tenantApi.postOne<LoginResponse>('/auth/login', credentials);
    },
    onSuccess: (data) => {
      // If backend requires 2FA, do NOT populate the store — the caller
      // is expected to prompt for a TOTP code and invoke useChallenge2FA.
      if (isTwoFactorChallenge(data)) {
        return;
      }

      tenantAuthStore.getState().login(data.employee, data.tenant);
      // D35 — another user's unsaved boat edits never reach this one; the
      // signed-in user's own (kept across an expired session) stay.
      purgePendingBoatChanges({ tenantId: data.tenant.id, employeeId: data.employee.id });
      queryClient.invalidateQueries({ queryKey: ['currentEmployee'] });
    },
    onError: (error: any) => {
      console.error('Login error:', error.response?.data || error.message);
    },
  });
}

/**
 * Complete a 2FA-gated login by submitting a TOTP code or a recovery code.
 *
 * The backend's /auth/2fa/challenge endpoint reads the pending challenge from
 * the session set by the preceding /auth/login call — no challenge token is
 * passed client-side. On success the response shape matches a normal login
 * and we populate Zustand identically.
 */
export function useChallenge2FA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TwoFactorChallengePayload) => {
      return tenantApi.postOne<AuthenticatedLoginResponse>(
        '/auth/2fa/challenge',
        payload,
      );
    },
    onSuccess: (data) => {
      tenantAuthStore.getState().login(data.employee, data.tenant);
      purgePendingBoatChanges({ tenantId: data.tenant.id, employeeId: data.employee.id });
      queryClient.invalidateQueries({ queryKey: ['currentEmployee'] });
    },
    onError: (error: any) => {
      console.error('2FA challenge error:', error.response?.data || error.message);
    },
  });
}

/**
 * Tenant logout hook
 */
export const useTenantLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Standard logout call - Axios handles XSRF token automatically
      await tenantApi.post('/auth/logout');
    },
    onSuccess: () => {
      // D35 — a deliberate logout forgets unsaved boat changes (a shared
      // computer's next user must not see them). The dead-session paths
      // (tenantApi onSessionDead / 401, centrifuge 401) call only the store's
      // logout and keep them for the return trip.
      purgePendingBoatChanges();
      tenantAuthStore.getState().logout();
      queryClient.clear();
      toast.info('Logged out successfully');
    },
    onError: () => {
      // Even if API call fails, clear local state
      purgePendingBoatChanges();
      tenantAuthStore.getState().logout();
      queryClient.clear();
    },
  });
};

/**
 * Get current authenticated employee from API
 */
export const useCurrentEmployee = () => {
  const isAuthenticated = tenantAuthStore((state) => state.isAuthenticated);

  const query = useQuery({
    queryKey: ['currentEmployee'],
    queryFn: () => tenantApi.getOne<{ employee: Employee; tenant: Tenant }>('/auth/me'),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Sync employee/tenant to store whenever /auth/me returns fresh data.
  // React Query only produces a new `query.data` reference when the fetched
  // payload actually changes, so this effect fires on real updates — not on
  // every render. Previously this was an identity-only check (`id !== id`)
  // which meant any field change on the current user (e.g. two_factor_confirmed_at
  // after enabling 2FA) never reached the Zustand store.
  useEffect(() => {
    if (query.data) {
      tenantAuthStore.getState().setEmployee(query.data.employee);
      tenantAuthStore.getState().setTenant(query.data.tenant);
      // Hydrate the preferences store with the server's truth whenever /auth/me resolves.
      usePreferencesStore.getState().setAll(
        (query.data.employee.preferences ?? {}) as PreferencesTree
      );
    }
  }, [query.data]);

  return query;
};
